import type { ContactsView } from '@/pages/StaffContactsView.types'
import { initialEventView } from '@/pages/StaffEventsView'
import { incomeDrillDownFromSearch } from '@/lib/incomeInvoiceDrillDown'
import { agingInvoiceDrillDownFromSearch } from '@/lib/agingInvoiceDrillDown'

export function initialStaffView(): ContactsView {
  const path = window.location.pathname
  const preview = /^\/contacts\/organizations\/(\d+)\/company-preview$/.exec(path)
  if (preview) return { kind: 'organization-dashboard-preview', contactId: Number(preview[1]) }
  if (/^\/settings\/connections\/\d+$/.test(path))
    return { kind: 'connection-detail', connectionId: Number(path.split('/').at(-1)) }
  if (path === '/finance/invoices') {
    return {
      kind: 'finance-invoices',
      drillDown: incomeDrillDownFromSearch(window.location.search),
      agingContact: agingInvoiceDrillDownFromSearch(window.location.search),
    }
  }
  const dynamicView = initialDynamicView(path)
  if (dynamicView) return dynamicView
  if (path.startsWith('/settings/accounting')) return { kind: 'accounting-settings' }
  return initialViewsByPath[path] ?? { kind: 'list' }
}

function initialDynamicView(path: string): ContactsView | undefined {
  const courseView = initialCourseView(path)
  if (courseView) return courseView
  const eventView = initialEventView(path)
  if (eventView) return eventView
  if (path.startsWith('/contacts/persons/')) return numericView(path, 'person-profile', 'contactId')
  if (path.startsWith('/contacts/organizations/'))
    return numericView(path, 'organization-profile', 'contactId')
  if (path.startsWith('/contacts/chapters/'))
    return numericView(path, 'chapter-detail', 'chapterId')
  if (path.startsWith('/communication/blasts/'))
    return numericView(path, 'communication-blast-report', 'blastId')
  if (path === '/communication/audiences/segments/new') return { kind: 'communication-segment-new' }
  if (path.startsWith('/communication/audiences/segments/'))
    return numericView(path, 'communication-segment-detail', 'segmentId')
  if (path.startsWith('/communication/audiences/'))
    return numericView(path, 'communication-group-detail', 'groupId')
  if (path.startsWith('/settings/discount-codes/'))
    return numericView(path, 'discount-code-detail', 'discountCodeId')
  if (path.startsWith('/finance/invoices/')) return numericView(path, 'invoice-detail', 'invoiceId')
  if (path.startsWith('/finance/donations/campaigns/'))
    return numericView(path, 'finance-admin-campaign-detail', 'campaignId')
  if (path.startsWith('/finance/donations/'))
    return numericView(path, 'finance-campaign-detail', 'campaignId')
}

function initialCourseView(path: string): ContactsView | undefined {
  if (/^\/courses\/\d+$/.test(path)) return numericView(path, 'course-detail', 'courseId')
  if (/^\/courses\/\d+\/edit$/.test(path))
    return numericView(path.replace(/\/edit$/, ''), 'course-edit', 'courseId')
}

function numericView(path: string, kind: string, idKey: string): ContactsView | undefined {
  const id = Number(path.split('/').at(-1))
  if (!Number.isInteger(id) || id <= 0) return undefined
  return { kind, [idKey]: id } as ContactsView
}

const initialViewsByPath: Record<string, ContactsView> = {
  '/reports/event-registrations': { kind: 'event-registrations' },
  '/settings/connections': { kind: 'connections' },
  '/settings/connections/unmatched': { kind: 'unmatched-items' },
  '/events': { kind: 'events-list' },
  '/courses': { kind: 'courses-list' },
  '/courses/import': { kind: 'enrolment-import' },
  '/courses/new': { kind: 'course-new' },
  '/events/new': { kind: 'event-new' },
  '/members': { kind: 'members' },
  '/members/organizations': { kind: 'organization-memberships' },
  '/members/levels': { kind: 'membership-levels' },
  '/members/renewal-pipeline': { kind: 'renewal-pipeline' },
  '/members/engagement': { kind: 'engagement' },
  '/settings/renewals': { kind: 'renewal-settings' },
  '/settings/renewals/arming-review': { kind: 'renewal-arming-review' },
  '/finance': { kind: 'finance-overview' },
  '/finance/financial-history': { kind: 'financial-history' },
  '/finance/invoices': { kind: 'finance-invoices' },
  '/finance/donations': { kind: 'finance-donations' },
  '/finance/donations/campaigns': { kind: 'finance-campaigns' },
  '/finance/autopay-recovery': { kind: 'finance-autopay-recovery' },
  '/finance/tax-report': { kind: 'finance-tax-report' },
  '/finance/income-report': { kind: 'finance-income-report' },
  '/finance/collections-report': { kind: 'finance-collections-report' },
  '/finance/membership-report': { kind: 'finance-membership-report' },
  '/finance/aging': { kind: 'finance-aging-report' },
  '/finance/recognition-schedule': { kind: 'finance-recognition-schedule' },
  '/finance/payouts': { kind: 'finance-payouts' },
  '/communication/email-log': { kind: 'communication-email-log' },
  '/communication/blasts': { kind: 'communication-blasts' },
  '/communication/audiences': { kind: 'communication-audiences' },
  '/finance/cant-reach': { kind: 'finance-unreachable-members' },
  '/settings/custom-fields': { kind: 'custom-fields' },
  '/settings/payments': { kind: 'payment-settings' },
  '/settings/finance': { kind: 'finance-settings' },
  '/settings/taxes': { kind: 'tax-settings' },
  '/settings/discount-codes': { kind: 'discount-codes' },
  '/settings/email': { kind: 'email-identity-settings' },
  '/settings/system-emails': { kind: 'system-email-settings' },
  '/settings/email-templates': { kind: 'system-email-templates' },
  '/settings/engagement-weights': { kind: 'engagement-weights' },
  '/admin/directory/settings': { kind: 'admin-directory-settings' },
  '/admin/directory/preview': { kind: 'admin-directory-preview' },
  '/settings/seats': { kind: 'seats' },
  '/settings/join-form': { kind: 'join-form-settings' },
  '/settings/organization-dashboard': { kind: 'organization-dashboard-setting' },
  '/settings/learning': { kind: 'credit-unit-settings' },
  '/contacts/duplicates': { kind: 'duplicates' },
  '/contacts/chapters': { kind: 'chapters' },
  '/contacts/import': { kind: 'import' },
}
